import DomainSummary from "@/components/DomainSummary";
import ServerServices from "@/components/ServerServices";
import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Plus, Globe, Server, MoreVertical, Copy, Download, Trash2, Power,
  Wrench, UploadCloud, FileArchive, Loader2, GitBranch, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState, fmtBytes } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

function DeployDialog({ open, setOpen, onDone }) {
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [repoUrl, setRepoUrl] = useState("");
  const [branch, setBranch] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const submit = async () => {
    if (!name || !file) { toast.error("Name and file required"); return; }
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("name", name); fd.append("file", file);
    try {
      const r = await api.post("/deploy", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r);
      toast.success(`Deployed as ${r.detected_type} — ${r.site.slug}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deploy failed");
    } finally { setBusy(false); }
  };

  const submitGit = async () => {
    if (!name || !repoUrl) { toast.error("Name and repo URL required"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/deploy-git", { name, repo_url: repoUrl, branch: branch || null });
      setResult(r);
      toast.success(`Cloned & deployed as ${r.detected_type} — ${r.site.slug}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Git deploy failed");
    } finally { setBusy(false); }
  };

  const reset = () => { setName(""); setFile(null); setRepoUrl(""); setBranch(""); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogContent className="bg-[#0c0c0e] border-zinc-800" data-testid="deploy-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><UploadCloud className="h-4 w-4" /> Deploy Site / App</DialogTitle>
        </DialogHeader>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My Game"
            data-testid="deploy-name-input" className="mt-1.5 bg-zinc-900 border-zinc-800" />
        </div>
        <Tabs defaultValue="upload" className="mt-2">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="upload" data-testid="tab-upload">Upload ZIP</TabsTrigger>
            <TabsTrigger value="git" data-testid="tab-git">From Git</TabsTrigger>
          </TabsList>
          <TabsContent value="upload" className="space-y-3 mt-3">
            <button onClick={() => inputRef.current?.click()} data-testid="deploy-file-btn"
              className="w-full flex items-center gap-2 rounded-md border border-dashed border-zinc-700 bg-zinc-900 px-3 py-4 text-sm text-zinc-400 hover:border-zinc-600">
              <FileArchive className="h-4 w-4" /> {file ? file.name : "Choose .zip or .html…"}
            </button>
            <input ref={inputRef} type="file" accept=".zip,.html,.htm" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0])} data-testid="deploy-file-input" />
            <p className="text-xs text-zinc-600 font-mono">Auto-detects Static vs Node · 250MB cap</p>
            <button onClick={submit} disabled={busy} data-testid="deploy-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-zinc-100 text-zinc-900 px-4 py-2 text-sm font-medium hover:bg-white disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Deploy
            </button>
          </TabsContent>
          <TabsContent value="git" className="space-y-3 mt-3">
            <Input value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)} placeholder="https://github.com/user/repo.git"
              data-testid="git-url-input" className="bg-zinc-900 border-zinc-800 font-mono text-sm" />
            <Input value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="branch (optional)"
              data-testid="git-branch-input" className="bg-zinc-900 border-zinc-800 font-mono text-sm" />
            <p className="text-xs text-zinc-600 font-mono">Shallow clone · auto-detect · npm deps for Node</p>
            <button onClick={submitGit} disabled={busy} data-testid="git-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-zinc-100 text-zinc-900 px-4 py-2 text-sm font-medium hover:bg-white disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitBranch className="h-4 w-4" />} Clone & Deploy
            </button>
          </TabsContent>
        </Tabs>
        {result && (
          <pre className="max-h-40 overflow-auto rounded-md border border-zinc-800 bg-black p-3 text-xs font-mono text-zinc-400 whitespace-pre-wrap">
            {result.install_log || "Deployed."}
          </pre>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Sites() {
  const [sites, setSites] = useState(null);
  const [deployOpen, setDeployOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();

  const load = () => api.get("/sites").then(setSites).catch(() => setSites([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("new")) { setDeployOpen(true); setParams({}); } }, [params]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  if (!sites) return <Loading label="Loading sites" />;

  return (
    <div data-testid="sites-page">
      <DomainSummary />
      <PageHeader
        title="Sites"
        breadcrumb="cannon / sites"
        subtitle={`${sites.length} site${sites.length === 1 ? "" : "s"} · studyformath.site`}
        actions={canWrite && (
          <button onClick={() => setDeployOpen(true)} data-testid="new-site-btn"
            className="inline-flex items-center gap-2 rounded-md bg-zinc-100 text-zinc-900 px-3 py-1.5 text-sm font-medium hover:bg-white transition-colors">
            <Plus className="h-4 w-4" /> Deploy
          </button>
        )}
      />
      <DeployDialog open={deployOpen} setOpen={setDeployOpen} onDone={load} />

      {sites.length === 0 ? (
        <EmptyState icon={Globe} title="No sites yet">Deploy your first ZIP to get started.</EmptyState>
      ) : (
        <div className="border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-900/50 text-left text-xs font-semibold uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-2.5">Site</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">URL</th>
                <th className="px-4 py-2.5">Size</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {sites.map((s) => (
                <tr key={s.slug} className="border-b border-zinc-800/70 hover:bg-zinc-900/40 transition-colors" data-testid={`site-row-${s.slug}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {s.type === "node" ? <Server className="h-4 w-4 text-violet-400" /> : <Globe className="h-4 w-4 text-sky-400" />}
                      <div>
                        <div className="text-zinc-100">{s.name}</div>
                        <div className="font-mono text-xs text-zinc-500">{s.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={s.type} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{s.slug}.studyformath.site</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{fmtBytes(s.size_bytes)}</td>
                  <td className="px-4 py-3">
                    {s.maintenance ? <StatusBadge status="STOPPED" /> :
                      s.type === "node" ? <StatusBadge status={s.process?.status || "STOPPED"} /> :
                      s.enabled ? <StatusBadge status="RUNNING" /> : <StatusBadge status="STOPPED" />}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger data-testid={`site-menu-${s.slug}`} className="text-zinc-500 hover:text-zinc-200">
                        <MoreVertical className="h-4 w-4" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#0c0c0e] border-zinc-800">
                        <DropdownMenuItem onClick={() => navigate(`/files?site=${s.slug}`)}>Open Files</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(`https://${s.slug}.studyformath.site`, "_blank")} data-testid={`preview-site-${s.slug}`}>
                          <ExternalLink className="h-3.5 w-3.5 mr-2" /> Open Live
                        </DropdownMenuItem>
                        {s.type === "node" && <DropdownMenuItem onClick={() => navigate(`/apps`)}>App Controls</DropdownMenuItem>}
                        {canWrite && <>
                          <DropdownMenuItem onClick={() => act(() => api.patch(`/sites/${s.slug}`, { enabled: !s.enabled }), s.enabled ? "Disabled" : "Enabled")}>
                            <Power className="h-3.5 w-3.5 mr-2" /> {s.enabled ? "Disable" : "Enable"}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => act(() => api.patch(`/sites/${s.slug}`, { maintenance: !s.maintenance }), "Toggled maintenance")}>
                            <Wrench className="h-3.5 w-3.5 mr-2" /> {s.maintenance ? "Exit" : "Enter"} Maintenance
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => act(() => api.post(`/sites/${s.slug}/duplicate`), "Duplicated")}>
                            <Copy className="h-3.5 w-3.5 mr-2" /> Duplicate
                          </DropdownMenuItem>
                        </>}
                        <DropdownMenuItem onClick={() => { window.location.href = `${API}/sites/${s.slug}/export`; }}>
                          <Download className="h-3.5 w-3.5 mr-2" /> Export ZIP
                        </DropdownMenuItem>
                        {canWrite && <>
                          <DropdownMenuSeparator className="bg-zinc-800" />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-rose-400 focus:text-rose-400">
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-[#0c0c0e] border-zinc-800">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete {s.name}?</AlertDialogTitle>
                                <AlertDialogDescription>This removes all files, versions and backups permanently.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
                                <AlertDialogAction data-testid={`confirm-delete-${s.slug}`} onClick={() => act(() => api.del(`/sites/${s.slug}`), "Deleted")}
                                  className="bg-rose-600 hover:bg-rose-500">Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ServerServices />
    </div>
  );
}
